import { Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';
import type { EvaluationReportForComputerScience } from '../../../Types/interfaces';
import {
  getDocumentationGrade,
  getGainedKnowledgeGrade,
  getKnowledgeGrade,
} from '../utils/calculations';

const { useBreakpoint } = Grid;

interface ComputerScienceEvaluationReportDisplayComponentProps {
  evaluationReport: EvaluationReportForComputerScience;
}

export const ComputerScienceEvaluationReportDisplayComponent = ({
  evaluationReport,
}: ComputerScienceEvaluationReportDisplayComponentProps): JSX.Element => {
  const { t } = useTranslation();
  const screens = useBreakpoint();

  if (!evaluationReport) {
    return <div>{t('evalReportDisplay.notFound')}</div>;
  }

  return (
    <Descriptions
      title={t('evalReportDisplay.specKnowledge')}
      bordered
      column={screens.xl ? 2 : 1}
      layout={screens.xs ? 'vertical' : 'horizontal'}
      labelStyle={
        screens.xs
          ? {
              fontWeight: 700,
            }
          : {}
      }
    >
      {/* this section of code is common with MathAndMathComputerScienceEvaluationReportDisplayComponent,
    because the ant design didn't allow to use ComputerScienceEvalutaionReportDisplayComponent inside
    the parent descriptions component, so I had to duplicate the code */}
      <Descriptions.Item label={t('evalReportDisplay.knowledgeAtStart')}>
        {getKnowledgeGrade(evaluationReport.studentsKnowledgeWhenStarting, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.qoa')}>
        {getKnowledgeGrade(evaluationReport.qualityOfWork, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.qod')}>
        {getDocumentationGrade(evaluationReport.qualityOfDocumentation, t)}
      </Descriptions.Item>

      <Descriptions.Item label={t('evalReportDisplay.algsAndDS')}>
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForAlgorithmsAndDataStructures,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.langsAndDevEnvs')}>
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForProgrammingLanguagesAndEnvironments,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.softArch')}>
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForSoftwareArchitectures,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.softMethods')}>
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForSoftwareMethodologies,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.databases')}>
        {getGainedKnowledgeGrade(evaluationReport.pointsForDatabases, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.opsys')}>
        {getGainedKnowledgeGrade(
          evaluationReport.pointsForOperatingSystemsAndComputerNetworks,
          t
        )}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.testing')}>
        {getGainedKnowledgeGrade(evaluationReport.pointsForTesting, t)}
      </Descriptions.Item>
      <Descriptions.Item label={t('evalReportDisplay.security')}>
        {getGainedKnowledgeGrade(evaluationReport.pointsForSecurity, t)}
      </Descriptions.Item>
      {evaluationReport.pointsForOtherDevelopmentSkills !== 0 && (
        <Descriptions.Item
          label={
            t('evalReportDisplay.other') +
            ': ' +
            evaluationReport.otherDevelopmentSkills
          }
        >
          {getGainedKnowledgeGrade(
            evaluationReport.pointsForOtherDevelopmentSkills,
            t
          )}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
};
